import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import type { SearchPreviewState } from '#/components/search/searchPagination';
import { useFocusTrap } from '#/hooks/useFocusTrap';
import { cn } from '#/utils/cn';

import {
  CategoryNavigationCta,
  CategoryNavigationItemsList,
} from './ItemLists';
import { DisclosureIcon, OutlineChevron, SearchAffordance } from './shared';
import type { CategoryNavigationItem } from './types';
import { useMobileNavigationState } from './useMobileNavigationState';

interface MobileNavigationProps {
  title: string;
  searchLabel: string;
  searchQuery?: string;
  previewSearchState?: SearchPreviewState;
  enableSearchPreview?: boolean;
  searchPrompt: string;
  productTypes: CategoryNavigationItem[];
  sectors: CategoryNavigationItem[];
  cta?: CategoryNavigationItem;
  browseEyebrow: string;
  activeHref?: string;
  expandedByDefault: boolean;
  productTypesExpandedByDefault: boolean;
  sectorsExpandedByDefault: boolean;
  onSearchSubmit?: (query: string) => void;
  onSearchClick?: () => void;
  isPinned: boolean;
  isInteractive: boolean;
  isSearchPending?: boolean;
}

function MobileInfoCard({
  eyebrow,
  heading,
  description,
}: {
  eyebrow: string;
  heading: string;
  description: string;
}) {
  return (
    <div className="rounded-4 border-gray-30 flex flex-col gap-8 border px-20 py-20">
      <p className="text-fg-utility text-static-12 font-semibold uppercase">
        {eyebrow}
      </p>
      <h3 className="text-fg-subheader text-static-18 leading-normal font-normal">
        {heading}
      </h3>
      <p className="text-slate-60 text-static-14 leading-normal">
        {description}
      </p>
    </div>
  );
}

function MobileSubsection({
  title,
  panelId,
  expanded,
  onToggle,
  children,
}: {
  title: string;
  panelId: string;
  expanded: boolean;
  onToggle: () => void;
  children?: ReactNode;
}) {
  return (
    <section className="w-full pt-12 first:pt-0">
      <h2>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={onToggle}
          className="text-blue-90 rounded-4 text-static-16 flex items-center gap-8 px-8 py-8 text-left leading-normal font-semibold"
        >
          <span>{title}</span>
          <OutlineChevron expanded={expanded} />
        </button>
      </h2>
      {expanded ? (
        <div id={panelId} className="pt-12">
          {children}
        </div>
      ) : null}
    </section>
  );
}

export function MobileNavigation({
  title,
  searchLabel,
  searchQuery,
  previewSearchState,
  enableSearchPreview,
  searchPrompt,
  productTypes,
  sectors,
  cta,
  browseEyebrow,
  activeHref,
  expandedByDefault,
  productTypesExpandedByDefault,
  sectorsExpandedByDefault,
  onSearchSubmit,
  onSearchClick,
  isPinned,
  isInteractive,
  isSearchPending,
}: MobileNavigationProps) {
  const headerRef = useRef<HTMLDivElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const suppressToggleRefocusRef = useRef(false);
  const {
    isExpanded,
    productTypesExpanded,
    sectorsExpanded,
    closeExpanded,
    overlayContentStyle,
    toggleExpanded,
    toggleProductTypesExpanded,
    toggleSectorsExpanded,
  } = useMobileNavigationState({
    headerRef,
    overlayRef,
    expandedByDefault,
    productTypesExpandedByDefault,
    sectorsExpandedByDefault,
    isPinned,
    isInteractive,
    activeHref,
  });

  useFocusTrap({
    active: isExpanded && isInteractive,
    containerRef: overlayRef,
    returnFocusRef: toggleButtonRef,
    resolveReturnFocus: () => {
      if (suppressToggleRefocusRef.current) {
        suppressToggleRefocusRef.current = false;
        return false;
      }
    },
    allowOutsideClick: true,
    escapeDeactivates: true,
    onDeactivate: closeExpanded,
  });

  const overlay = (
    <div
      ref={overlayRef}
      id="category-navigation-mobile-panel"
      aria-hidden={isExpanded ? undefined : true}
      className={cn(
        'border-blue-20 bg-gray-10 absolute top-[calc(100%+1px)] right-0 left-0 z-0 h-[calc(100dvh-100%-1px)] overflow-y-auto overscroll-y-contain border-b shadow-[0_8px_16px_rgba(0,52,83,0.08)] motion-reduce:transition-none',
        isExpanded
          ? 'visible opacity-100 [transition:opacity_150ms_cubic-bezier(0.2,0,0,1),visibility_0s_linear_0s]'
          : 'pointer-events-none invisible opacity-0 [transition:opacity_150ms_cubic-bezier(0.2,0,0,1),visibility_0s_linear_150ms]',
      )}
    >
      <div
        style={overlayContentStyle}
        className="bg-gray-10 flow flow-12 min-h-full px-12 pt-12 pb-20 transition-[transform] duration-150 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none"
      >
        <MobileSubsection
          title="By Investment Product"
          panelId="category-navigation-mobile-product-types"
          expanded={productTypesExpanded}
          onToggle={toggleProductTypesExpanded}
        >
          <MobileInfoCard
            eyebrow={browseEyebrow}
            heading="Investment Product"
            description="Understand categories like ETFs, Mutual Funds, Stocks, etc."
          />
          <CategoryNavigationItemsList
            items={productTypes}
            activeHref={activeHref}
            variant="mobileCards"
            extraItem={<CategoryNavigationCta cta={cta} variant="mobile" />}
          />
        </MobileSubsection>
        <MobileSubsection
          title="By Sector"
          panelId="category-navigation-mobile-sectors"
          expanded={sectorsExpanded}
          onToggle={toggleSectorsExpanded}
        >
          <MobileInfoCard
            eyebrow={browseEyebrow}
            heading="Sector"
            description="Categories based on similar products or activities."
          />
          <CategoryNavigationItemsList
            items={sectors}
            activeHref={activeHref}
            variant="mobileLinks"
          />
        </MobileSubsection>
      </div>
    </div>
  );

  return (
    <nav
      aria-label="Category navigation mobile"
      className="border-blue-20 bg-gray-10 relative z-40 border-b"
    >
      <div
        ref={headerRef}
        className="bg-gray-10 relative z-10 grid items-stretch px-20 py-20 transition-[grid-template-columns,gap] duration-200 ease-out motion-reduce:transition-none"
        style={{
          gridTemplateColumns: isSearchExpanded
            ? '0px minmax(0, 1fr)'
            : '1fr minmax(0, 10rem)',
          columnGap: isSearchExpanded ? '0px' : '12px',
        }}
      >
        <div
          className={cn(
            'min-w-0 overflow-hidden transition-[max-width,opacity,transform] duration-200 ease-out motion-reduce:transition-none',
            isSearchExpanded
              ? 'pointer-events-none -translate-x-8 opacity-0'
              : 'translate-x-0 opacity-100',
          )}
          style={{
            maxWidth: isSearchExpanded ? '0px' : undefined,
            maxHeight: isSearchExpanded ? '0px' : undefined,
          }}
        >
          <button
            ref={toggleButtonRef}
            type="button"
            aria-hidden={isSearchExpanded}
            aria-expanded={isExpanded}
            aria-controls="category-navigation-mobile-panel"
            onClick={toggleExpanded}
            tabIndex={isSearchExpanded ? -1 : undefined}
            className="text-fg-header text-static-14 rounded-4 flex h-full w-full items-center gap-8 py-12 text-left leading-normal font-semibold whitespace-nowrap"
          >
            <span className="text-balance">{title}</span>
            <DisclosureIcon expanded={isExpanded} />
          </button>
        </div>
        <div
          data-mobile-search-expanded={isSearchExpanded ? 'true' : 'false'}
          className={cn(
            'w-full justify-self-end transition-[max-width,transform] duration-200 ease-out motion-reduce:transition-none',
            isSearchExpanded ? 'max-w-full translate-x-0' : 'max-w-160',
          )}
        >
          <SearchAffordance
            searchLabel={searchLabel}
            searchPrompt={searchPrompt}
            query={searchQuery}
            searchState={previewSearchState}
            enablePreview={enableSearchPreview}
            trackContext="search_bar"
            condensed
            showLeadingLabel={false}
            showActions={isSearchExpanded}
            onSubmitQuery={onSearchSubmit}
            isPending={isSearchPending}
            onClick={onSearchClick}
            onPointerDownWithin={() => {
              if (isExpanded) {
                suppressToggleRefocusRef.current = true;
                closeExpanded();
              }
            }}
            onFocusWithin={() => {
              setIsSearchExpanded(true);
              onSearchClick?.();
            }}
            onBlurWithin={() => setIsSearchExpanded(false)}
          />
        </div>
      </div>
      {overlay}
    </nav>
  );
}
